import { Linking, Platform, ScrollView, Text, View } from "react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  AlertCircle,
  DollarSign,
  ExternalLink,
  PiggyBank,
} from "lucide-react-native";
import {
  CardSurface,
  SectionTitleRow,
  BackLink,
  TextActionButton,
} from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import type {
  CostPaymentRecordParam,
  CostsStackParamList,
  MainTabParamList,
} from "../../types";
import {
  useLocale,
  useRegion,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import ThiingsIcon from "../../../shared/ThiingsIcon";
import type { ThiingsIconName } from "../../../shared/icons";
import { useSmartBack } from "../../hooks/useSmartBack";
import { type InfoSection } from "../../components/InfoSheet";
import SpendingAccountCard, {
  type SpendingAccount,
} from "../../components/SpendingAccountCard";
import PaymentEntryTypePicker from "../../components/PaymentEntryTypePicker";
import {
  CostDocumentUploadModal,
  type ManualPaymentEntry,
  type UploadedCostDocument,
} from "./suggested/CostsOverviewConceptScreen";
import {
  useCreatePaymentRecord,
  usePaymentRecords,
  useUpdatePaymentRecord,
} from "@src/packages/costs/hooks/usePaymentRecords";
import {
  useSaveTaxDeductionProfile,
  useSaveTaxDeductionReview,
  useDeleteTaxDeductionReview,
  useTaxDeductionCalculation,
  useTaxDeductionProfile,
  useTaxDeductionReviews,
} from "@src/packages/costs/hooks/useTaxDeductions";
import {
  MEDICAL_DEDUCTION_TAX_YEAR,
  calculateMedicalExpenseDeduction,
} from "@src/packages/costs/medicalExpenseDeduction";
import {
  buildTaxReviewSummary,
  type TaxReviewEntry,
} from "@src/packages/costs/taxReview";
import {
  parseDecimalAmount,
  toDecimalAmount,
} from "@src/packages/costs/decimalAmount";
import {
  attachDocumentToPaymentRecord,
  buildManualPaymentRecordPayload,
  buildUploadedPaymentRecordPayload,
  detachDocumentFromPaymentRecord,
  findPaymentRecordForUpload,
  itemizationChoice,
  itemizationValue,
  type ItemizationChoice,
} from "./taxReviewHelpers";
import {
  apiToUiPaymentRecord,
  parseDateText,
  todayIsoDate,
  uiToPaymentRecordUpdates,
} from "./paymentRecordMapping";
import PaymentRecordDetailModal from "./PaymentRecordDetailModal";
import { getDevTaxReviewFixture } from "./taxReviewFixtures";
import TaxReviewSections from "./TaxReviewSections";
import {
  claimEffectiveLabel,
  isFinancialClaimCurrent,
  TAX_BENEFITS,
  type FinancialClaim,
  type TaxBenefit,
} from "@src/packages/costs/financialContent";
import { FINANCIAL_CLAIM_ICONS } from "./financialClaimIcons";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<CostsStackParamList, "TaxDeductions">;

/** Where a claim applies, when it takes effect and when it was last checked. */
function claimProvenanceLine(
  claim: FinancialClaim,
  t: TranslationFunction,
  formatDate: (isoDate: string) => string,
): string {
  return `${claim.provenance.jurisdiction} · ${claimEffectiveLabel(claim, t, formatDate)} · Verified ${formatDate(claim.provenance.lastVerifiedOn)}`;
}

function buildTaxSourceSections(
  benefit: TaxBenefit,
  t: TranslationFunction,
  formatDate: (isoDate: string) => string,
): InfoSection[] {
  return [
    {
      title: benefit.summary.label,
      icon: FINANCIAL_CLAIM_ICONS[benefit.summary.kind],
      body: [
        benefit.summary.value,
        benefit.summary.caveat,
        claimProvenanceLine(benefit.summary, t, formatDate),
        `Source: ${benefit.summary.provenance.sourceUrl}`,
      ]
        .filter(Boolean)
        .join("\n\n"),
    },
    ...benefit.eligible
      .filter((claim) => claim.kind !== "account-rule")
      .map((claim) => ({
        title: claim.label,
        icon: FINANCIAL_CLAIM_ICONS[claim.kind],
        body: [
          claim.value,
          claim.caveat,
          claimProvenanceLine(claim, t, formatDate),
          `Source: ${claim.provenance.sourceUrl}`,
        ]
          .filter(Boolean)
          .join("\n\n"),
      })),
  ];
}

function SourceLink({ claim }: { claim: FinancialClaim }) {
  return (
    <TextActionButton
      label="IRS source"
      accessibilityLabel={`Open IRS source for ${claim.label}`}
      size="xs"
      icon={ExternalLink}
      iconPosition="trailing"
      onPress={() => {
        void Linking.openURL(claim.provenance.sourceUrl);
      }}
      style={{ paddingVertical: 0, paddingHorizontal: 0 }}
    />
  );
}

function ClaimRow({ claim }: { claim: FinancialClaim }) {
  const t = useTranslations();
  const { long: formatLongDate } = useDateFormat();
  return (
    <View
      style={{
        paddingTop: 10,
        paddingBottom: 10,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            fontSize: 11,
            color: EMBER.colors.textFaint,
            textTransform: "uppercase",
            letterSpacing: 0.35,
          }}
        >
          {claim.label}
        </Text>
        <SourceLink claim={claim} />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: EMBER.colors.text,
          lineHeight: 19,
          marginTop: 3,
        }}
      >
        {claim.value}
      </Text>
      {claim.caveat && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: EMBER.colors.textMid,
            lineHeight: 17,
            marginTop: 3,
          }}
        >
          {claim.caveat}
        </Text>
      )}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          color: EMBER.colors.textFaint,
          marginTop: 4,
        }}
      >
        {claimProvenanceLine(claim, t, formatLongDate)}
      </Text>
    </View>
  );
}

function TaxBenefitCard({ benefit }: { benefit: TaxBenefit }) {
  const summaryCurrent = isFinancialClaimCurrent(benefit.summary);
  const currentEligible = benefit.eligible.filter((claim) =>
    isFinancialClaimCurrent(claim),
  );
  const benefitCurrent =
    summaryCurrent && currentEligible.length === benefit.eligible.length;

  return (
    <CardSurface style={{ padding: 20 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <ThiingsIcon name={benefit.icon as ThiingsIconName} size={28} />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {benefit.title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              marginTop: 1,
            }}
          >
            {benefit.subtitle}
          </Text>
        </View>
      </View>

      {benefitCurrent ? (
        <>
          <ClaimRow claim={benefit.summary} />

          <View style={{ marginTop: 12, marginBottom: 12 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: EMBER.colors.textMid,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                marginBottom: 2,
              }}
            >
              What qualifies
            </Text>
            {currentEligible.map((claim) => (
              <View key={claim.id}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                    paddingTop: 8,
                  }}
                >
                  <DollarSign
                    size={13}
                    color={EMBER.colors.textMid}
                    strokeWidth={1.5}
                    style={{ marginTop: 2 }}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: EMBER.colors.text,
                      lineHeight: 18,
                    }}
                  >
                    <Text style={{ fontWeight: "600" }}>{claim.label}: </Text>
                    {claim.value}
                  </Text>
                </View>
                {claim.caveat && (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      color: EMBER.colors.textMid,
                      lineHeight: 17,
                      marginLeft: 21,
                      marginTop: 2,
                    }}
                  >
                    {claim.caveat}
                  </Text>
                )}
                <View style={{ marginLeft: 21, marginTop: 3 }}>
                  <SourceLink claim={claim} />
                </View>
              </View>
            ))}
          </View>

          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textMid,
              lineHeight: 17,
              fontStyle: "italic",
            }}
          >
            {benefit.note}
          </Text>
        </>
      ) : (
        <View style={{ paddingVertical: 10 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textMid,
              lineHeight: 19,
              marginBottom: 6,
            }}
          >
            Current details are unavailable until this guidance is reviewed.
          </Text>
          <SourceLink claim={benefit.summary} />
        </View>
      )}
    </CardSurface>
  );
}

export default function TaxDeductionsScreen({ navigation, route }: Props) {
  const region = useRegion();
  const { locale } = useLocale();
  const t = useTranslations();
  const { long: formatLongDate } = useDateFormat();
  const taxYear = MEDICAL_DEDUCTION_TAX_YEAR;
  const paymentRecordsQuery = usePaymentRecords();
  const taxProfileQuery = useTaxDeductionProfile(taxYear);
  const taxReviewsQuery = useTaxDeductionReviews(taxYear);
  const taxCalculationQuery = useTaxDeductionCalculation(taxYear);
  const saveTaxProfile = useSaveTaxDeductionProfile(taxYear);
  const saveTaxReview = useSaveTaxDeductionReview(taxYear);
  const deleteTaxReview = useDeleteTaxDeductionReview(taxYear);
  const createPaymentRecordMutation = useCreatePaymentRecord();
  const updatePaymentRecordMutation = useUpdatePaymentRecord();
  const [addTypePickerVisible, setAddTypePickerVisible] = useState(false);
  const [selectedAddKind, setSelectedAddKind] = useState<
    ManualPaymentEntry["kind"] | null
  >(null);
  const [selectedPaymentRecord, setSelectedPaymentRecord] =
    useState<CostPaymentRecordParam | null>(null);
  const [attachmentRecordId, setAttachmentRecordId] = useState<string | null>(
    null,
  );
  const [taxReviewAgiDraft, setTaxReviewAgiDraft] = useState<number | null>(
    null,
  );
  const [taxReviewItemizationDraft, setTaxReviewItemizationDraft] =
    useState<ItemizationChoice>("unknown");
  const [taxReviewInlineInputError, setTaxReviewInlineInputError] = useState<
    string | null
  >(null);
  const taxReviewInlineSaveTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const devTaxReviewFixture = useMemo(
    () => getDevTaxReviewFixture(taxYear),
    [taxYear],
  );
  const taxReviewSummary = useMemo(
    () =>
      buildTaxReviewSummary(
        devTaxReviewFixture?.records ?? paymentRecordsQuery.data ?? [],
        devTaxReviewFixture?.reviews ?? taxReviewsQuery.data ?? [],
        devTaxReviewFixture?.profile ?? taxProfileQuery.data ?? null,
        taxYear,
      ),
    [
      devTaxReviewFixture,
      paymentRecordsQuery.data,
      taxProfileQuery.data,
      taxReviewsQuery.data,
      taxYear,
    ],
  );
  const medicalExpenseBenefit = TAX_BENEFITS.find(
    (benefit) => benefit.id === "medical-expense-deduction",
  );
  const taxReviewInfoSections = medicalExpenseBenefit
    ? buildTaxSourceSections(medicalExpenseBenefit, t, formatLongDate)
    : [];
  const taxReviewCalculation = useMemo(
    () =>
      calculateMedicalExpenseDeduction(
        taxReviewSummary.entries.map((entry) => entry.candidate),
        taxReviewAgiDraft,
        itemizationValue(taxReviewItemizationDraft),
      ),
    [taxReviewAgiDraft, taxReviewItemizationDraft, taxReviewSummary.entries],
  );
  useEffect(() => {
    const profile = devTaxReviewFixture?.profile ?? taxProfileQuery.data;
    setTaxReviewAgiDraft(parseDecimalAmount(profile?.adjustedGrossIncome));
    setTaxReviewItemizationDraft(
      itemizationChoice(profile?.itemizesDeductions),
    );
    setTaxReviewInlineInputError(null);
  }, [devTaxReviewFixture?.profile, taxProfileQuery.data]);
  useEffect(
    () => () => {
      if (taxReviewInlineSaveTimeoutRef.current) {
        clearTimeout(taxReviewInlineSaveTimeoutRef.current);
      }
    },
    [],
  );
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });
  const openSpendingAccountChat = (account: SpendingAccount) => {
    rootNavigation?.navigate("Chat", {
      screen: "Assistant",
      params: {
        initialPrompt: `I want to use my ${account} dollars for fertility care. Help me figure out what I can submit or reimburse. First check whether I have already uploaded an itemized bill, receipt, explanation of benefits, or plan document. Ask me for photos or uploads for anything missing, then help me identify what to confirm with my benefits administrator before I pay or submit it.`,
        autoSendInitialPrompt: true,
        returnTab: "Costs",
      },
    });
  };
  const scheduleTaxReviewInputs = (
    adjustedGrossIncome: number | null,
    itemizesDeductions: boolean | null,
  ) => {
    if (taxReviewInlineSaveTimeoutRef.current) {
      clearTimeout(taxReviewInlineSaveTimeoutRef.current);
    }

    if (adjustedGrossIncome === null) {
      setTaxReviewInlineInputError(t("costs.tax_review.invalid_agi"));
      return;
    }

    setTaxReviewInlineInputError(null);
    if (
      parseDecimalAmount(taxProfileQuery.data?.adjustedGrossIncome) ===
        adjustedGrossIncome &&
      taxProfileQuery.data?.itemizesDeductions === itemizesDeductions
    ) {
      return;
    }

    taxReviewInlineSaveTimeoutRef.current = setTimeout(() => {
      void saveTaxProfile
        .mutateAsync({
          taxYear,
          adjustedGrossIncome: toDecimalAmount(adjustedGrossIncome),
          itemizesDeductions,
        })
        .catch((error: unknown) => {
          setTaxReviewInlineInputError(
            error instanceof Error
              ? error.message
              : t("costs.tax_review.save_tax_inputs_error"),
          );
        });
    }, 350);
  };

  const handleTaxReviewAgiChange = (value: number | null) => {
    setTaxReviewAgiDraft(value);
    scheduleTaxReviewInputs(value, itemizationValue(taxReviewItemizationDraft));
  };

  const handleTaxReviewItemizationChange = (value: ItemizationChoice) => {
    setTaxReviewItemizationDraft(value);
    scheduleTaxReviewInputs(taxReviewAgiDraft, itemizationValue(value));
  };

  const openPaymentDetails = (entry: TaxReviewEntry) => {
    setSelectedPaymentRecord(apiToUiPaymentRecord(entry.record, locale));
  };

  const selectedTaxReviewEntry = selectedPaymentRecord
    ? taxReviewSummary.entries.find(
        (entry) => entry.record.id === selectedPaymentRecord.id,
      )
    : undefined;
  const selectedTaxReviewDecision =
    selectedTaxReviewEntry?.reviewStatus === "included"
      ? "include"
      : selectedTaxReviewEntry?.reviewStatus === "excluded"
        ? "exclude"
        : "unknown";
  // The Include/Exclude buttons and the summary they feed only make sense for
  // records dated within the current review year. For records outside that
  // window we hide the decision controls entirely so users don't tap into a
  // no-op.
  const selectedRecordInTaxYear = selectedPaymentRecord
    ? (
        (devTaxReviewFixture?.records ?? paymentRecordsQuery.data ?? []).find(
          (record) => record.id === selectedPaymentRecord.id,
        )?.occurredOn ?? ""
      ).startsWith(`${taxYear}-`)
    : false;

  const attachDocumentToRecord = async (document: UploadedCostDocument) => {
    if (!attachmentRecordId) throw new Error("No payment record selected");
    // Pull the raw record from the unfiltered query — summary.entries is
    // filtered to the review year, so records dated outside it (manual adds
    // from other years) wouldn't be found and the attach would error.
    const rawRecord = (
      devTaxReviewFixture?.records ??
      paymentRecordsQuery.data ??
      []
    ).find((candidate) => candidate.id === attachmentRecordId);
    if (!rawRecord) throw new Error("Payment record is no longer available");
    if (!document.documentId || !document.uploadId) {
      throw new Error(t("costs.tax_review.missing_document_linkage"));
    }

    const documents = attachDocumentToPaymentRecord(
      rawRecord,
      document,
      t("costs.tax_review.missing_document_linkage"),
    );
    const updated = await updatePaymentRecordMutation.mutateAsync({
      paymentRecordId: rawRecord.id,
      updates: { documents },
    });
    setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
    setAttachmentRecordId(null);
    await paymentRecordsQuery.refetch();
  };

  const createManualPaymentRecord = async (entry: ManualPaymentEntry) => {
    const refreshedRecords = entry.attachmentUploadId
      ? await paymentRecordsQuery.refetch()
      : null;
    const existing = entry.attachmentUploadId
      ? findPaymentRecordForUpload(
          refreshedRecords?.data ?? [],
          entry.attachmentUploadId,
        )
      : undefined;
    if (existing) return;

    await createPaymentRecordMutation.mutateAsync(
      buildManualPaymentRecordPayload(entry, {
        missingDocumentLinkageMessage: t(
          "costs.tax_review.missing_document_linkage",
        ),
        parseDate: parseDateText,
        today: todayIsoDate(),
      }),
    );
  };

  const createUploadedPaymentRecord = async (
    document: UploadedCostDocument,
  ) => {
    const refreshedRecords = await paymentRecordsQuery.refetch();
    const existing = document.uploadId
      ? findPaymentRecordForUpload(
          refreshedRecords.data ?? [],
          document.uploadId,
        )
      : undefined;
    if (existing) return;

    await createPaymentRecordMutation.mutateAsync(
      buildUploadedPaymentRecordPayload(document, {
        missingDocumentLinkageMessage: t(
          "costs.tax_review.missing_document_linkage",
        ),
        uploadedDocumentLabel: (kind) => `Uploaded ${kind} document`,
        uploadedPhotoLabel: "Uploaded photo",
        uploadedFileLabel: "Uploaded file",
        today: todayIsoDate(),
      }),
    );
  };
  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout
      overlay={
        <>
          <PaymentEntryTypePicker
            visible={addTypePickerVisible}
            onClose={() => setAddTypePickerVisible(false)}
            onSelect={(kind) => {
              setSelectedAddKind(kind);
              setAddTypePickerVisible(false);
            }}
          />
          <CostDocumentUploadModal
            visible={selectedAddKind != null}
            mode="payment"
            initialPaymentKind={selectedAddKind ?? undefined}
            onClose={() => setSelectedAddKind(null)}
            onDocumentPicked={createUploadedPaymentRecord}
            onManualPaymentAdded={createManualPaymentRecord}
          />
          <CostDocumentUploadModal
            visible={attachmentRecordId !== null}
            mode="payment"
            documentOnly
            onClose={() => setAttachmentRecordId(null)}
            onDocumentPicked={attachDocumentToRecord}
            onManualPaymentAdded={async () => undefined}
          />
        </>
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <BackLink
          label={route.params?.returnTab ? t("you.shared.back") : "Costs"}
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: colors.deepBrown,
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          {t("costs.tax_deductions")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            marginBottom: 20,
          }}
        >
          U.S. federal guidance with tax years and official sources
        </Text>

        <CardSurface
          style={{
            padding: 16,
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <ThiingsIcon name="hourglass" size={28} />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.text,
              lineHeight: 19,
            }}
          >
            Keep your receipts and treatment records together, then use the
            guidance below to review what may qualify and what to confirm before
            filing.
          </Text>
        </CardSurface>

        <View style={{ marginBottom: 16 }}>
          <TaxReviewSections
            summary={taxReviewSummary}
            calculation={taxReviewCalculation}
            calculationSnapshot={taxCalculationQuery.data}
            locale={locale}
            agiDraft={taxReviewAgiDraft}
            itemizationDraft={taxReviewItemizationDraft}
            inlineInputError={taxReviewInlineInputError}
            onAgiChange={handleTaxReviewAgiChange}
            onItemizationChange={handleTaxReviewItemizationChange}
            onAddExpense={() => setAddTypePickerVisible(true)}
            onOpenDetails={openPaymentDetails}
            infoSections={taxReviewInfoSections}
            allRecords={
              devTaxReviewFixture?.records ?? paymentRecordsQuery.data ?? []
            }
            onOpenPaymentRecord={(record) =>
              setSelectedPaymentRecord(apiToUiPaymentRecord(record, locale))
            }
          />
        </View>

        <View style={{ gap: 16 }}>
          {TAX_BENEFITS.filter(
            (benefit) => benefit.id !== "medical-expense-deduction",
          ).map((benefit) =>
            benefit.id === "hsa-fsa" ? (
              <View key={benefit.id}>
                <SectionTitleRow
                  title="HSA / FSA"
                  icon={PiggyBank}
                  style={{ marginTop: 12 }}
                />
                <SpendingAccountCard
                  locale={locale}
                  onOpenChat={openSpendingAccountChat}
                />
              </View>
            ) : (
              <TaxBenefitCard key={benefit.id} benefit={benefit} />
            ),
          )}

          <View
            style={{
              flexDirection: "row",
              gap: 10,
              padding: 16,
            }}
          >
            <AlertCircle
              size={16}
              color={EMBER.colors.textFaint}
              strokeWidth={1.5}
              style={{ marginTop: 1 }}
            />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 12,
                color: EMBER.colors.textFaint,
                lineHeight: 18,
              }}
            >
              This information is educational and not tax advice. Consult a
              qualified tax professional about your specific situation.
            </Text>
          </View>
        </View>
      </ScrollView>

      {selectedPaymentRecord ? (
        <PaymentRecordDetailModal
          record={selectedPaymentRecord}
          locale={locale}
          onClose={() => setSelectedPaymentRecord(null)}
          onAttachDocument={() =>
            setAttachmentRecordId(selectedPaymentRecord.id)
          }
          onDeleteDocument={async (documentId) => {
            if (!selectedPaymentRecord) return;
            const rawRecord = (
              devTaxReviewFixture?.records ??
              paymentRecordsQuery.data ??
              []
            ).find((candidate) => candidate.id === selectedPaymentRecord.id);
            if (!rawRecord) return;
            const documents = detachDocumentFromPaymentRecord(
              rawRecord,
              documentId,
            );
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: rawRecord.id,
              updates: { documents },
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          onSaveRecord={async (record) => {
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: record.id,
              updates: uiToPaymentRecordUpdates(record),
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          taxEligibility={selectedPaymentRecord.taxEligibility}
          onTaxEligibilityChange={async (taxEligibility) => {
            if (!selectedPaymentRecord) return;
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: selectedPaymentRecord.id,
              updates: { taxEligibility },
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          taxReviewDecision={selectedTaxReviewDecision}
          onTaxReviewDecision={
            selectedRecordInTaxYear
              ? async (decision) => {
                  if (!selectedPaymentRecord) return;
                  await saveTaxReview.mutateAsync({
                    paymentRecordId: selectedPaymentRecord.id,
                    decision,
                  });
                }
              : undefined
          }
          onResetTaxReviewDecision={
            selectedRecordInTaxYear
              ? async () => {
                  if (!selectedPaymentRecord) return;
                  await deleteTaxReview.mutateAsync(selectedPaymentRecord.id);
                }
              : undefined
          }
        />
      ) : null}
    </GlassScreenLayout>
  );
}
